import {useEffect, useId, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import type {ApiError, CaptchaResponse} from "../../../shared/api/client";

const turnstileScriptID = "magistika-turnstile-api";
const turnstileScriptURL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileAPI {
  render(container: HTMLElement, options: TurnstileOptions): string;
  remove(widgetID: string): void;
}

interface TurnstileOptions {
  sitekey: string;
  action: string;
  language: "ru";
  theme: "auto";
  size: "flexible";
  callback(token: string): void;
  "expired-callback"(): void;
  "error-callback"(): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

export interface CaptchaChallengeDescriptor {
  provider: string;
  siteKey: string;
  action: string;
}

interface CaptchaChallengeProps {
  challenge: CaptchaChallengeDescriptor;
  attempt: number;
  onResponse(response: CaptchaResponse | null): void;
}

type WidgetStatus = "loading" | "ready" | "verified" | "expired" | "error";

let turnstileLoad: Promise<TurnstileAPI> | null = null;

export function CaptchaChallenge({challenge, attempt, onResponse}: CaptchaChallengeProps) {
  const {t} = useTranslation();
  const labelID = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const responseCallback = useRef(onResponse);
  const [widgetState, setWidgetState] = useState<{generation: string; status: WidgetStatus}>({generation: "", status: "loading"});
  const [retry, setRetry] = useState(0);
  const generation = `${attempt}\u0000${retry}\u0000${challenge.provider}\u0000${challenge.siteKey}\u0000${challenge.action}`;
  const status: WidgetStatus = challenge.provider !== "turnstile"
    ? "error"
    : widgetState.generation === generation
      ? widgetState.status
      : "loading";

  useEffect(() => {
    responseCallback.current = onResponse;
  }, [onResponse]);

  useEffect(() => {
    let active = true;
    let widgetID = "";
    responseCallback.current(null);

    if (challenge.provider !== "turnstile") {
      return () => responseCallback.current(null);
    }

    void loadTurnstile().then((api) => {
      if (!active || !containerRef.current) return;
      setWidgetState({generation, status: "ready"});
      widgetID = api.render(containerRef.current, {
        sitekey: challenge.siteKey,
        action: challenge.action,
        language: "ru",
        theme: "auto",
        size: "flexible",
        callback: (token) => {
          if (!active) return;
          responseCallback.current({provider: challenge.provider, token});
          setWidgetState({generation, status: "verified"});
        },
        "expired-callback": () => {
          if (!active) return;
          responseCallback.current(null);
          setWidgetState({generation, status: "expired"});
        },
        "error-callback": () => {
          if (!active) return;
          responseCallback.current(null);
          setWidgetState({generation, status: "error"});
        },
      });
      containerRef.current.focus();
    }).catch(() => {
      if (active) setWidgetState({generation, status: "error"});
    });

    return () => {
      active = false;
      responseCallback.current(null);
      if (widgetID && window.turnstile) window.turnstile.remove(widgetID);
    };
  }, [attempt, challenge.action, challenge.provider, challenge.siteKey, generation, retry]);

  const statusText = status === "loading"
    ? t("auth.captcha.loading")
    : status === "ready"
      ? t("auth.captcha.ready")
      : status === "verified"
        ? t("auth.captcha.verified")
        : status === "expired"
          ? t("auth.captcha.expired")
          : t("auth.captcha.error");

  return <section className="captcha-challenge" aria-labelledby={labelID}>
    <p id={labelID}>{t("auth.captcha.prompt")}</p>
    <div
      className="captcha-widget"
      ref={containerRef}
      tabIndex={-1}
      aria-label={t("auth.captcha.widgetLabel")}
    />
    <p className="captcha-status" role="status" aria-live="polite" aria-atomic="true">{statusText}</p>
    {status === "error" && <button className="button secondary" type="button" onClick={() => setRetry((value) => value + 1)}>
      {t("auth.captcha.retry")}
    </button>}
  </section>;
}

export function captchaChallengeFromError(error: ApiError): CaptchaChallengeDescriptor | null {
  if (error.code !== "captcha_required") return null;
  const provider = error.fields.captcha_provider?.trim() ?? "";
  const siteKey = error.fields.captcha_site_key?.trim() ?? "";
  const action = error.fields.captcha_action?.trim() ?? "";
  if (!provider || !siteKey || !action) return null;
  return {provider, siteKey, action};
}

function loadTurnstile(): Promise<TurnstileAPI> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileLoad) return turnstileLoad;

  turnstileLoad = new Promise<TurnstileAPI>((resolve, reject) => {
    const existing = document.getElementById(turnstileScriptID);
    const script = existing instanceof HTMLScriptElement ? existing : document.createElement("script");
    const fail = () => {
      script.remove();
      turnstileLoad = null;
      reject(new Error("Turnstile is unavailable"));
    };
    const ready = () => {
      if (!window.turnstile) {
        fail();
        return;
      }
      resolve(window.turnstile);
    };
    script.addEventListener("load", ready, {once: true});
    script.addEventListener("error", fail, {once: true});
    if (!existing) {
      script.id = turnstileScriptID;
      script.src = turnstileScriptURL;
      script.async = true;
      script.defer = true;
      script.referrerPolicy = "no-referrer";
      document.head.append(script);
    }
  });
  return turnstileLoad;
}
